import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Maximize, Heart, ArrowRight, GitCompare, ChevronLeft, ChevronRight, ImageIcon } from 'lucide-react';
import { formatNaira } from '../../data/mockData';
import { VerificationBadge, TierBadge } from './Badges';
interface PropertyCardProps {
  property: any;
}
export const PropertyCard = ({
  property
}: PropertyCardProps) => {
  const images: string[] = property.images && property.images.length > 0 ? property.images : [];
  const [activeIndex, setActiveIndex] = useState(0);
  const hasGallery = images.length > 1;
  const detailPath = `/property/${property.id}`;
  const handlePrev = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
  };
  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
  };
  return <div className="group bg-white rounded-2xl overflow-hidden shadow-soft border border-neutral-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col h-full" data-id="element-320">
      {/* Image Container — clickable, navigates to detail */}
      <Link to={detailPath} aria-label={`View details for ${property.title}`} className="relative aspect-[4/3] overflow-hidden bg-neutral-100 block cursor-pointer" data-id="element-321">
        {images.map((src, idx) => <img key={idx} src={src} alt={`${property.title} - photo ${idx + 1}`} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${idx === activeIndex ? 'opacity-100' : 'opacity-0'} group-hover:scale-105 transition-transform`} data-id="element-322" />)}

        {/* Top Left Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2 items-start z-10" data-id="element-323">
          <TierBadge tier={property.tier} data-id="element-324" />
          {property.isVerified && <VerificationBadge status="Approved" data-id="element-325" />}
        </div>

        {/* Top Right Actions */}
        <div className="absolute top-3 right-3 flex flex-col gap-2 z-10" data-id="element-326">
          <button type="button" onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }} className="p-2 bg-white/80 backdrop-blur-sm rounded-full text-text hover:text-error hover:bg-white transition-colors" data-id="element-327">
            <Heart size={18} data-id="element-328" />
          </button>
          <Link to={`/compare?ids=${property.id}`} onClick={e => e.stopPropagation()} className="p-2 bg-white/80 backdrop-blur-sm rounded-full text-text hover:text-secondary hover:bg-white transition-colors" data-id="element-329">
            <GitCompare size={18} data-id="element-330" />
          </Link>
        </div>

        {/* Gallery Arrows */}
        {hasGallery && <>
            <button type="button" onClick={handlePrev} aria-label="Previous photo" className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm text-primary-900 hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center shadow-md z-10" data-id="element-331">
              <ChevronLeft size={18} data-id="element-332" />
            </button>
            <button type="button" onClick={handleNext} aria-label="Next photo" className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm text-primary-900 hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center shadow-md z-10" data-id="element-333">
              <ChevronRight size={18} data-id="element-334" />
            </button>

            {/* Photo Counter */}
            <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-primary-900/70 backdrop-blur-sm text-white text-[10px] font-semibold px-2 py-1 rounded-full flex items-center gap-1 z-10" data-id="element-335">
              <ImageIcon size={10} data-id="element-336" />
              {activeIndex + 1} / {images.length}
            </div>
          </>}

        {/* Bottom Price + Size */}
        <div className="absolute bottom-12 left-3 right-3 flex justify-between items-end z-10" data-id="element-337">
          <div className="bg-primary-900/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg font-semibold shadow-lg" data-id="element-338">
            {formatNaira(property.price)}
          </div>
          <div className="bg-white/90 backdrop-blur-sm text-text-light px-2 py-1 rounded text-xs font-medium flex items-center gap-1" data-id="element-339">
            <Maximize size={12} data-id="element-340" />
            {property.size}
          </div>
        </div>

        {/* Progress Bar Strip */}
        {hasGallery && <div className="absolute bottom-2 left-3 right-3 flex gap-1.5 z-10" data-id="element-341">
            {images.map((_, idx) => <button key={idx} type="button" onMouseEnter={() => setActiveIndex(idx)} onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          setActiveIndex(idx);
        }} aria-label={`Preview photo ${idx + 1}`} className={`relative h-1.5 flex-grow rounded-full overflow-hidden transition-all ${idx === activeIndex ? 'bg-secondary' : 'bg-white/60 hover:bg-white/90'}`} data-id="element-342" />)}
          </div>}
      </Link>

      {/* Content */}
      <div className="p-5 flex flex-col flex-grow" data-id="element-343">
        <Link to={detailPath} className="block" data-id="element-344">
          <h3 className="font-serif text-lg font-semibold text-primary-900 line-clamp-2 leading-tight mb-2 hover:text-secondary transition-colors" data-id="element-345">
            {property.title}
          </h3>
        </Link>

        <div className="flex items-center text-text-light text-sm mb-3" data-id="element-346">
          <MapPin size={14} className="mr-1 text-secondary" data-id="element-347" />
          {property.district}, Abuja
        </div>

        {/* Thumbnail Gallery Row — each thumb is a Link to detail page */}
        {hasGallery && <div className="flex gap-1.5 mb-4" data-id="element-348">
            {images.slice(0, 4).map((src, idx) => <Link key={idx} to={detailPath} onMouseEnter={() => setActiveIndex(idx)} aria-label={`View ${property.title} — photo ${idx + 1}`} className={`relative w-1/4 aspect-[4/3] rounded-md overflow-hidden border-2 transition-all cursor-pointer ${idx === activeIndex ? 'border-secondary ring-1 ring-secondary/40' : 'border-transparent opacity-70 hover:opacity-100 hover:border-secondary/40'}`} data-id="element-349">
                <img src={src} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" data-id="element-350" />
              </Link>)}
          </div>}

        <div className="mt-auto pt-4 border-t border-neutral-100 flex items-center justify-between" data-id="element-351">
          <div className="text-xs text-text-light flex items-center gap-2" data-id="element-352">
            <span className="w-6 h-6 rounded-full bg-neutral-200 flex items-center justify-center text-primary-900 font-bold" data-id="element-353">
              {property.seller.name.charAt(0)}
            </span>
            <span className="truncate max-w-[100px]" data-id="element-354">
              {property.seller.name}
            </span>
          </div>
          <Link to={detailPath} className="text-sm font-medium text-primary-900 hover:text-secondary flex items-center gap-1 transition-colors" data-id="element-355">
            View Details <ArrowRight size={14} data-id="element-356" />
          </Link>
        </div>
      </div>
    </div>;
};